import { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { Paper, Button } from '@material-ui/core';
import { Visibility } from '@material-ui/icons';
import { useDispatch, useSelector } from 'react-redux';
import { Table } from 'rsuite';
import './ArchivedBidProfiles.scss';
import _ from 'lodash';
import { dynamicSort } from '@commonClient2/services/sort';
import { useAvailableHeight } from '@commonClient2/hooks';
import { BidStatusChip } from '@commonClient2/components';
import {
    getStatusDateLabel,
    searchBid,
    mapBidProfilesToTable
} from './ArchivedBidProfiles.service';
import getSortIcon from '../SortIcon';
import { fetchEmployeeArchiveAppData } from '../../store/appData';
import { selectedBiddedProfile } from '../../store/bidProfile';
import AssignedShiftlinePanel from '../AssignedShiftlineSelection/AssignedShiftlinePanel';

/** @typedef {'asc'|'desc'|undefined} SortType */
/** @type {SortType} */
const defaultSortType = 'asc';

const { Column, HeaderCell, Cell } = Table;
const widthMobileToggle = 993;
/**
 * @param {{search: string}} props
 */
function ArchivedBidProfiles(props) {
    const { search } = props;
    const { t } = useTranslation('shiftBidding', { keyPrefix: 'employee.archive' });
    const { t: tEmployeeCommon } = useTranslation('shiftBidding', { keyPrefix: 'employee.common' });
    const dispatch = useDispatch();
    
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    const isMobile = windowWidth < widthMobileToggle;

    /** @type {ShiftBidding.EmployeeStore} */
    // @ts-ignore
    const { employeeAppData } = useSelector((state) => state);
    const {
        archivedBidProfiles,
    } = employeeAppData;
    const mappedBidProfiles = archivedBidProfiles?.map(mapBidProfilesToTable);
    const profilesNumber = mappedBidProfiles?.length;

    const [filteredProfiles, setFilteredProfiles] = useState(mappedBidProfiles);
    const [sortValues, setSortValues] = useState({ sortColumn: 'name', sortType: defaultSortType });
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);

    useEffect(() => {
        dispatch(fetchEmployeeArchiveAppData());
    }, []);

    function handleFilter() {
        let filteredBids = mappedBidProfiles;
        // filter by search value
        if (search) {
            filteredBids = filteredBids.filter((bid) => searchBid(bid, search));
        }
        setFilteredProfiles(filteredBids);
    }

    /**
     * @param {string} dataKey
     * @param {SortType} sortType
     */
    const handleSort = (dataKey, sortType) => {
        /** @type {EmployeeBidProfile.IEmployeeArchiveBidProfile[]} */
        let sortedFilteredProfiles = [];
        const dynamicSortOrder = sortType === 'desc' ? '-' : '';
        const order = sortType === 'desc' ? -1 : 1;
        // eslint-disable-next-line max-len
        const nullCheckingDateSort = (/** @type {string} */ propName) => (/** @type {EmployeeBidProfile.IEmployeeArchiveBidProfile} */ obj1, /** @type {EmployeeBidProfile.IEmployeeArchiveBidProfile} */ obj2) => {
            const a = _.get(obj1, propName, null);
            const b = _.get(obj2, propName, null);
            if (!a && !b) {
                return 0;
            }
            if (!a) {
                return order * -1;
            }
            if (!b) {
                return order * 1;
            }
            const momentA = moment(a);
            const momentB = moment(b);
            // eslint-disable-next-line no-nested-ternary
            return order * ((momentA < momentB) ? -1 : (momentA > momentB) ? 1 : 0);
        };

        switch (dataKey) {
            case 'Name':
                sortedFilteredProfiles = filteredProfiles.sort(
                    dynamicSort(`${dynamicSortOrder}name`),
                );
                break;
            case 'archivedStatus':
                sortedFilteredProfiles = filteredProfiles.sort(
                    dynamicSort(`${dynamicSortOrder}archivedStatus`),
                );
                break;
            case 'Status Date':
                sortedFilteredProfiles = filteredProfiles.sort(
                    nullCheckingDateSort('statusDate'),
                );
                break;
            default:
                break;
        }
        setFilteredProfiles(_.cloneDeep(sortedFilteredProfiles));
    };

    useEffect(() => {
        handleSort(sortValues.sortColumn, sortValues.sortType);
    }, [JSON.stringify(sortValues)]);

    useEffect(() => {
        handleFilter();
    }, [search, JSON.stringify(mappedBidProfiles)]);
    
    /**
     * @param {EmployeeBidProfile.IEmployeeArchiveBidProfile} rowData
     */
    const handleView = async (rowData) => {
        await dispatch(selectedBiddedProfile(rowData.bidProfileId, false));
        setIsDrawerOpen(true);
    };

    const renderViewButton = (rowData) => (
        <div className="core--action-button-container">
            <Button
                // @ts-ignore
                onClick={() => handleView(rowData)}
                className="archived-bid-profiles-tablerow-action-button"
            >
                <Visibility fontSize="inherit" />
                <div className="view-button-label">{tEmployeeCommon('view')}</div>
            </Button>
        </div>
    );

    const ref = useRef(null);
    const height = useAvailableHeight(ref) || 0;

    return (
        <>
            <div className="archived-bid-profiles-list-full-page">
                <Paper
                    elevation={2}
                    className={!isMobile ? 'archived-bid-profiles-table-container' : 'archived-bid-profiles-table-container--mobile'}
                    ref={ref}
                >
                    <Table
                        sortColumn={sortValues.sortColumn}
                        sortType={sortValues.sortType}
                        onSortColumn={(sortColumn, sortType) => setSortValues({ sortColumn, sortType })}
                        height={height}
                        data={filteredProfiles}
                        id="archived-bid-profiles-table"
                        rowClassName="archived-bid-profiles-table-row"
                        className={isMobile ? 'archived-bid-profiles-table--mobile' : ''}
                        onRowClick={isMobile ? (rowData) => handleView(rowData) : () => {}}
                    >
                        <Column
                            flexGrow={2}
                            sortable
                            key="name"
                        >
                            <HeaderCell renderSortIcon={(sortType) => (getSortIcon(sortType))}>
                                {t('tableHeaders.name')}
                            </HeaderCell>
                            <Cell
                                dataKey="Name"
                                style={{ cursor: 'default' }}
                            >
                                {(
                                /** @type {import('rsuite/esm/Table').RowDataType<EmployeeBidProfile.IEmployeeArchiveBidProfile>} */ rowData
                                ) =>
                                    rowData.name}
                            </Cell>
                        </Column>
                        <Column
                            flexGrow={2}
                            sortable
                        >
                            <HeaderCell renderSortIcon={(sortValues) => (getSortIcon(sortValues))}>
                                {t('tableHeaders.status')}
                            </HeaderCell>
                            <Cell
                                dataKey="archivedStatus"
                                style={{ cursor: 'default' }}
                            >
                                {(
                                /** @type {import('rsuite/esm/Table').RowDataType<EmployeeBidProfile.IEmployeeArchiveBidProfile>} */ rowData
                                ) => 
                                    (
                                        <BidStatusChip
                                            statusName={rowData.archivedStatus}
                                        />
                                    )}
                            </Cell>
                        </Column>
                        <Column
                            flexGrow={2}
                            sortable
                        >
                            <HeaderCell renderSortIcon={(sortType) => (getSortIcon(sortType))}>
                                {t('tableHeaders.statusDate')}
                            </HeaderCell>
                            <Cell
                                dataKey="Status Date"
                                style={{ cursor: 'default' }}
                            >
                                {(
                                /** @type {import('rsuite/esm/Table').RowDataType<EmployeeBidProfile.IEmployeeArchiveBidProfile>} */ rowData
                                ) =>
                                    getStatusDateLabel(rowData.statusDate)}
                            </Cell>
                        </Column>
                        {
                            (!isMobile && (
                                <Column
                                    flexGrow={1}
                                    fixed="right"
                                >
                                    <HeaderCell> </HeaderCell>
                                    <Cell>
                                        { (rowData) => renderViewButton(rowData) }
                                    </Cell>
                                </Column>
                            ))
                        }
                    </Table>
                </Paper>
            </div>
            <AssignedShiftlinePanel
                open={isDrawerOpen}
                onClose={() => setIsDrawerOpen(false)}
                isMobile={isMobile}
            />
        </>
    );
}

export default ArchivedBidProfiles;
